import React, { useState, useEffect } from 'react';
import { getFarmers, getLivestock, getLivestockServices, getAiSuggestion } from '../services/api';
import Loader from '../components/Loader';

const HomePage = () => {
  const [farmers, setFarmers] = useState([]);
  const [livestock, setLivestock] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [selectedLivestock, setSelectedLivestock] = useState(null);
  const [livestockServices, setLivestockServices] = useState([]);
  const [symptoms, setSymptoms] = useState('');
  const [image, setImage] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchFarmers = async () => {
      try {
        const response = await getFarmers();
        setFarmers(response.data.data.farmers);
      } catch (error) {
        console.error('Error fetching farmers:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchFarmers();
  }, []);

  const handleFarmerSelect = async (farmerId) => {
    setSelectedFarmer(farmerId);
    setSelectedLivestock(null);
    setLivestockServices([]);
    setAiSuggestion(null);
    setShowDetails(false);
    setLoading(true);
    try {
      const response = await getLivestock(farmerId);
      setLivestock(response.data.data.livestock);
    } catch (error) {
      console.error('Error fetching livestock:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLivestockSelect = async (animal) => {
    setSelectedLivestock(animal);
    setAiSuggestion(null);
    setShowDetails(false);
    setLoading(true);
    try {
      const response = await getLivestockServices(selectedFarmer, animal.id);
      setLivestockServices(response.data.data.services);
    } catch (error) {
      console.error('Error fetching livestock services:', error);
      setLivestockServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGetAiSuggestion = async () => {
    setLoading(true);
    setAiSuggestion(null);
    setShowDetails(false);
    try {
      const suggestion = await getAiSuggestion(selectedLivestock, symptoms, image, livestockServices);
      setAiSuggestion(suggestion);
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
    }
    setLoading(false);
  };

  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  if (initialLoading) {
    return <Loader />;
  }

  return (
    <div className="container mt-5">
      <div className="row">
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h3>Select Farmer</h3>
            </div>
            <div className="card-body">
              <ul className="list-group">
                {farmers.map((farmer) => (
                  <li
                    key={farmer.id}
                    className={`list-group-item ${selectedFarmer === farmer.id ? 'active' : ''}`}
                    onClick={() => handleFarmerSelect(farmer.id)}
                  >
                    {farmer.first_name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {selectedFarmer && (
            <div className="card mt-4">
              <div className="card-header">
                <h3>Livestock</h3>
              </div>
              <div className="card-body">
                {loading ? <Loader /> : (
                  <ul className="list-group">
                    {livestock.map((animal) => (
                      <li
                        key={animal.id}
                        className={`list-group-item ${selectedLivestock?.id === animal.id ? 'active' : ''}`}
                        onClick={() => handleLivestockSelect(animal)}
                      >
                        {animal.name} ({animal.livestock_type_name})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="col-md-8">
          {!selectedLivestock && (
            <div className="card">
              <div className="card-body text-center">
                <img src="/assets/placeholder.png" alt="AI placeholder" className="img-fluid rounded mb-3" />
                <p className="card-text">
                  {selectedFarmer
                    ? 'Select a livestock to view details and get AI suggestions.'
                    : 'Please select a farmer to view their livestock.'}
                </p>
              </div>
            </div>
          )}
          {selectedLivestock && (
            <>
              {loading ? <Loader /> : (
                <>
                  {livestockServices.length > 0 && (
                    <div className="card">
                      <div className="card-header">
                        <h3>Service History</h3>
                      </div>
                      <div className="card-body">
                        <table className="table">
                          <thead>
                            <tr>
                              <th scope="col">Service</th>
                              <th scope="col">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {livestockServices
                              .slice()
                              .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))
                              .map((service, index) => (
                                <tr key={index}>
                                  <td>{service.service_name}</td>
                                  <td>{new Date(service.visit_date).toLocaleDateString()}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div className={`card ${livestockServices.length > 0 ? 'mt-4' : ''}`}>
                    <div className="card-header">
                      <h3>Get AI Suggestion</h3>
                    </div>
                    <div className="card-body">
                      <p><strong>Name:</strong> {selectedLivestock.name}</p>
                      <p><strong>Type:</strong> {selectedLivestock.livestock_type_name}</p>
                      <p><strong>Age:</strong> {selectedLivestock.age}</p>
                      {selectedLivestock.breed && <p><strong>Breed:</strong> {selectedLivestock.breed}</p>}
                      <div className="mb-3">
                        <textarea
                    className="form-control"
                    rows="5"
                    placeholder="e.g., Animal is lethargic, has a cough, and is not eating."
                    value={symptoms}
                    onChange={(e) => setSymptoms(e.target.value)}
                    maxLength={500}
                  />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="image" className="form-label">Upload Image (Optional)</label>
                        <input type="file" className="form-control" id="image" accept="image/*" onChange={handleImageChange} />
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={handleGetAiSuggestion}
                        disabled={loading}
                      >
                        Get AI Suggestion
                      </button>
                      {loading && <Loader loading={loading} />}
                      {aiSuggestion && (
                        <div className="mt-3">
                          <div>
                            <h5>Summary</h5>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {typeof aiSuggestion.summary === 'string'
                                ? aiSuggestion.summary
                                : JSON.stringify(aiSuggestion.summary, null, 2)}
                            </pre>
                          </div>
                          <button className="btn btn-secondary btn-sm" onClick={() => setShowDetails(!showDetails)}>
                            {showDetails ? 'Hide Details' : 'Show Details'}
                          </button>
                          {showDetails && (
                            <div className="mt-2">
                              <h5>Details</h5>
                              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {typeof aiSuggestion.detailed === 'string'
                                  ? aiSuggestion.detailed
                                  : JSON.stringify(aiSuggestion.detailed, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;